import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import axios from 'axios';
import TopBar from './TopBar';
import TopNav from './TopNav';
import LiveClassBar from './LiveClassBar';
import LeftNav from './LeftNav';
import { useUiStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { useFeatureStore } from '../../stores/featureStore';
import { me } from '../../services/authApi';
import { ShellTitleContext } from './ShellTitleContext';
import { useFocusTrap } from '../ui/useFocusTrap';

// Mail-style shell: fixed TopBar on top, LeftNav on the left (static on desktop,
// off-canvas drawer on mobile), scrollable main area rendering the route Outlet.
export default function AppShell() {
  const navCollapsed = useUiStore((s) => s.navCollapsed);
  const mobileNavOpen = useUiStore((s) => s.mobileNavOpen);
  const setMobileNavOpen = useUiStore((s) => s.setMobileNavOpen);
  const focusMode = useUiStore((s) => s.focusMode);
  const setFocusMode = useUiStore((s) => s.setFocusMode);
  const [title, setTitle] = useState<string | undefined>();

  // WIP-007 deferred #D4, now closed. The off-canvas drawer is a modal in
  // every way that matters — full-screen overlay, the page behind it inert —
  // but it had no dialog semantics, no focus trap, no Escape and no
  // focus-return, so a keyboard user opening it tabbed straight through the
  // invisible page underneath. The audit deferred it only because the
  // top-nav redesign had this file frozen; that redesign has since landed
  // (BP 10 0.6.0), so this is the same ~10-line hook the ten dialogs use.
  // Its Escape cannot collide with the focus-mode Escape below: the drawer
  // renders under `!focusMode`, so the two are never mounted together.
  const drawerRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(drawerRef, () => setMobileNavOpen(false), mobileNavOpen && !focusMode);

  // Escape always leaves focus mode. Focus mode hides every navigation
  // control, so without a keyboard route out of it a user who could not find
  // the floating Exit button — or who lost it behind a stage that went
  // fullscreen — would be stuck on a screen with no visible way back.
  // Registered only while focus mode is on, so it never competes with a
  // dialog's or a drawer's own Escape handling.
  useEffect(() => {
    if (!focusMode) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFocusMode(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusMode, setFocusMode]);

  // Boot bootstrap for authenticated routes: the auth store persists only the
  // token, so after a hard reload the user object is null. Re-hydrate it from
  // /auth/me (this is also what surfaces is_admin for the Platform Admin link).
  // Also load the public feature-flag set used to gate UI across the app.
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useAuthStore((s) => s.logout);
  const loadFeatures = useFeatureStore((s) => s.load);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  useEffect(() => {
    if (token && !user) {
      me()
        .then(setUser)
        .catch((err) => {
          // A pending forced password change (BP 26) 403s EVERY requireAuth
          // route, including this one — apiClient's own interceptor already
          // sends the user to /account/password for that case (WIP-003 found
          // this: without the guard below, this catch-all raced that redirect
          // and logged the user out from under it — a student who merely
          // browsed to an AppShell route before changing their temporary
          // password landed back at /login instead of the password screen).
          // A dead/invalid token resolves to 401 → apiClient already redirects
          // there too; clearing here covers any OTHER failure so we don't loop
          // on a stale user.
          const code = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
          if (code === 'password_change_required') return;
          logout();
        });
    }
  }, [token, user, setUser, logout]);

  return (
    <ShellTitleContext.Provider value={{ setTitle }}>
      <div className="flex h-screen [height:100dvh] flex-col overflow-hidden bg-ws-bg">
        {/* WIP-007 (docs/AUDIT-ACCESSIBILITY.md #9) — a keyboard user landing
            on any authenticated page otherwise has to tab through the whole
            TopBar + LeftNav (which can run to dozens of links) before
            reaching the actual page (WCAG 2.4.1 Bypass Blocks). Additive
            only — doesn't touch TopBar/LeftNav's own markup, so it's safe
            alongside the parallel top-nav work. */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[100] focus:rounded-md focus:bg-ws-accent focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-ws-on-accent"
        >
          Skip to main content
        </a>
        {/* Focus mode hides the destination row, the live-class row, the
            sidebar and the lecture's slide rail — but NOT this branding bar,
            which keeps the control that leaves it.
            Instructor: "never use floating icons and change the one for full
            screen and add it to top branding bar". The earlier version
            unmounted this row too and floated an Exit button over the content;
            a single slim bar that never moves is both less chrome and a more
            findable way out.
            Rows are unmounted rather than styled out: a hidden-but-present
            TopNav still holds focusable links, and tabbing into navigation you
            cannot see is worse than having none. */}
        <TopBar title={title} />
        {/* Row 2 — every destination named, always visible (design/Main.dc.html).
            Independent of navCollapsed: focus mode hides the week tree below,
            not this. */}
        {!focusMode && <TopNav />}
        {/* Row 3 — instructor-only, appears only while a class is live, on
            every page except the run sheet (which already covers it in full —
            see LiveClassBar.tsx). */}
        {!focusMode && <LiveClassBar />}

        <div className="flex flex-1 overflow-hidden">
          {/* Desktop sidebar */}
          {!navCollapsed && !focusMode && (
            <div className="hidden lg:flex shrink-0">
              <LeftNav />
            </div>
          )}

          {/* Mobile off-canvas drawer */}
          {mobileNavOpen && !focusMode && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div
                className="ws-fade-in absolute inset-0 bg-ws-overlay"
                onClick={() => setMobileNavOpen(false)}
              />
              <div
                ref={drawerRef}
                role="dialog" aria-modal="true" aria-label="Navigation" tabIndex={-1}
                className="ws-slide-in-left absolute inset-y-0 left-0 shadow-lg focus:outline-none"
              >
                <LeftNav onNavigate={() => setMobileNavOpen(false)} />
              </div>
            </div>
          )}

          {/* Main scroll area */}
          <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto overflow-x-hidden bg-ws-bg focus:outline-none">
            <Outlet />
          </main>
        </div>
      </div>
    </ShellTitleContext.Provider>
  );
}
